
/**
 * Module dependencies.
 */

import { FormErrorsBasePathContext } from './form-errors-base-path-context';
import { FormProvider } from '@seegno/react-forms';
import { ReactNode, useCallback } from 'react';
import { isFunction } from 'lodash';

/**
 * Export `Submit` type.
 */

export type Submit = (values: any, actions: { reset: () => void }) => Promise<any> | null | undefined;

/**
 * `Validate` type.
 */

type Validate = (schema: any, values: any, options?: any) => any;

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  className?: string;
  errorsBasePath?: string;
  initialValues?: any;
  jsonSchema: any;
  onFormValuesChanged?: (value: any) => void;
  onSubmit: Submit;
  stateReducer?: (state: any, action: any) => any;
  validate?: Validate;
  validationOptions?: any;
};

/**
 * Export `Form` component.
 */

export function Form(props: Props): JSX.Element {
  const {
    children,
    errorsBasePath,
    initialValues,
    jsonSchema,
    onFormValuesChanged,
    onSubmit,
    stateReducer,
    validate,
    validationOptions,
    ...rest
  } = props;

  const handleSubmit = useCallback((values: any, ...restArgs: [any]) => {
    const normalizedValues = Object.entries(values).reduce((result: Record<string, any>, [key, value]) => {
      result[key] = isFunction((value as any)?.toJSON) ? (value as any).toJSON() : value;

      return result;
    }, {});

    return onSubmit(normalizedValues, ...restArgs);
  }, [onSubmit]);

  return (
    <FormErrorsBasePathContext.Provider value={errorsBasePath ?? null}>
      <FormProvider
        initialValues={initialValues}
        jsonSchema={jsonSchema}
        onFormValuesChanged={onFormValuesChanged}
        onSubmit={handleSubmit}
        stateReducer={stateReducer}
        validate={validate}
        validationOptions={validationOptions}
      >
        {({ submit }: { submit: () => void }) => (
          <form
            {...rest}
            onSubmit={submit}
          >
            {children}
          </form>
        )}
      </FormProvider>
    </FormErrorsBasePathContext.Provider>
  );
}
